import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { useToolCategories } from '../api/catalog'
import { guidanceFor, toEnvelope } from '../api/errors'
import { qk } from '../api/queries'
import type {
  ConnectorHint,
  ExecuteToolRequest,
  ExecuteToolResponse,
  ToolCategory,
  ToolSearchResponse,
  ToolSearchResult,
} from '../api/types'
import { useSession } from '../context/SessionContext'
import { isEntitlementDenial, refreshOnEntitlementDenial } from '../entitlements'
import { UpsellLink } from '../entitlements/PlanNote'
import { CodeBlock } from '../components/CodeBlock'
import { CopyButton } from '../components/CopyButton'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDateTime } from '../components/format'
import { Badge, EmptyState, ErrorPanel, Loading, NotAuthorized, PageHeader } from '../components/ui'
import { SchemaForm } from './playground/SchemaForm'
import { asSchema, initialDraft, toArguments, type Draft } from './playground/schema'
import { SNIPPET_TABS, snippetsFor, type SnippetTab } from './playground/snippets'
import {
  HISTORY_LIMIT,
  RISK_BADGE,
  connectionBadge,
  fetchToolSchema,
  hintStatusLabel,
  readHistory,
  riskOf,
  runBlock,
  statusOf,
  summaryOf,
  withSchema,
  writeHistory,
  type RunRecord,
} from './playground/tools'

export const SEARCH_DEBOUNCE_MS = 250
const SEARCH_LIMIT = 25

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function formatLatency(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`
}

let runSeq = 0
const newRunId = () => `run-${Date.now().toString(36)}-${(runSeq++).toString(36)}`

// ---------------------------------------------------------------------------

function catLabel(slug: string, vocab: ToolCategory[]) {
  return vocab.find((c) => c.slug === slug)?.display_name ?? slug
}

function ToolList({
  query,
  results,
  selected,
  onSelect,
  vocab,
}: {
  query: string
  results: ToolSearchResult[]
  selected: string | null
  onSelect: (t: ToolSearchResult) => void
  vocab: ToolCategory[]
}) {
  if (results.length === 0) {
    return query.trim() ? (
      <div className="state-panel state-panel--quiet"><p>No tools match “{query.trim()}”.</p></div>
    ) : (
      <EmptyState
        icon="grid"
        title="No tools available yet"
        body="Tools appear here once a toolkit is enabled for this workspace. Connect one in Toolkits, then come back to try it."
        action={<Link to="/toolkits" className="btn btn--primary"><Icon name="grid" size={14} /> Browse toolkits</Link>}
      />
    )
  }
  return (
    <ul className="pg-tools" aria-label="Tools">
      {results.map((t) => {
        const conn = connectionBadge(t.connection_status)
        return (
          <li key={t.name}>
            <button
              type="button"
              className={'pg-tool' + (selected === t.name ? ' active' : '')}
              aria-pressed={selected === t.name}
              onClick={() => onSelect(t)}
            >
              <span className="pg-tool-main">
                <code>{t.name}</code>
                <span className="pg-tool-desc">{summaryOf(t.description)}</span>
              </span>
              <span className="pg-tool-meta">
                {t.server_type && <span className="cell-sub">{t.server_type}</span>}
                {(t.categories ?? []).map((c) => <Badge key={c}>{catLabel(c, vocab)}</Badge>)}
                {t.policy_status === 'denied' && <Badge tone="error">Policy denied</Badge>}
                <Badge tone={conn.tone}>{conn.label}</Badge>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/** SYMONE_SEARCH_TOOLS `available`: ≤5 matching apps that exist but aren't connected for you. */
function AvailableHints({ hints, vocab }: { hints: ConnectorHint[]; vocab: ToolCategory[] }) {
  if (hints.length === 0) return null
  return (
    <section className="pg-hints" aria-label="Available, not connected">
      <h2 className="rail-heading">Available, not connected</h2>
      <ul>
        {hints.map((h) => (
          <li key={h.connector} className="pg-hint">
            <div className="pg-hint-main">
              <span className="cell-strong">{h.display_name}</span>
              <span className="badge-row">
                <Badge tone="warning">{hintStatusLabel(h.status)}</Badge>
                {h.categories.map((c) => <Badge key={c}>{catLabel(c, vocab)}</Badge>)}
              </span>
            </div>
            <Link
              to={`/toolkits?connect=${encodeURIComponent(h.connector)}&search=${encodeURIComponent(h.connector)}`}
              className="btn btn--sm"
              aria-label={`Connect ${h.display_name}`}
            >
              Connect
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The gateway's error envelope {code, title, detail, remedy}, plus what to do about the code. */
function ErrorDetails({ error, toolkit }: { error: unknown; toolkit?: string }) {
  const env = toEnvelope(error)
  return (
    <div className="pg-error" role="alert" aria-label={`Error ${env.code}`}>
      <div className="badge-row">
        <Badge tone="error">{env.code}</Badge>
        <strong>{env.title}</strong>
      </div>
      <p className="sf-error">{env.detail}</p>
      {env.remedy && <p className="pg-remedy"><strong>Remedy:</strong> {env.remedy}</p>}
      {guidanceFor(env.code) && <p className="cell-sub">{guidanceFor(env.code)}</p>}
      {isEntitlementDenial(error) && (
        <p>
          <UpsellLink />
        </p>
      )}
      {env.code === 'connection_required' && toolkit && (
        <Link to={`/toolkits?connect=${encodeURIComponent(toolkit)}&search=${encodeURIComponent(toolkit)}`} className="btn btn--sm">
          Connect {toolkit}
        </Link>
      )}
    </div>
  )
}

function ConfirmRun({
  tool,
  args,
  onCancel,
  onConfirm,
}: {
  tool: ToolSearchResult
  args: Record<string, unknown>
  onCancel: () => void
  onConfirm: () => void
}) {
  const risk = riskOf(tool)
  const destructive = risk === 'destructive'
  const [ack, setAck] = useState(false)
  const toolkit = tool.server_type || 'the connected app'
  return (
    <Modal
      title={`Run ${tool.name}?`}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className={destructive ? 'btn btn--danger' : 'btn btn--primary'}
            disabled={destructive && !ack}
            onClick={onConfirm}
          >
            {destructive ? 'Run destructive tool' : 'Run tool'}
          </button>
        </>
      }
    >
      <p className="warn-note" role="note">This will run against your real connected account.</p>
      {destructive ? (
        <p className="danger-note" role="note">
          <strong>This tool is marked destructive.</strong> It can delete or overwrite data in {toolkit}, and
          Symone can’t undo it.
        </p>
      ) : risk === 'unknown' ? (
        <p>This tool doesn’t declare whether it’s read-only, so treat it as one that changes data in {toolkit}.</p>
      ) : (
        <p>This tool can create or change data in {toolkit}.</p>
      )}
      <div>
        <p className="sf-label">Arguments</p>
        <pre className="pg-args">{JSON.stringify(args, null, 2)}</pre>
      </div>
      {destructive && (
        <label className="check">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          I understand this can’t be undone
        </label>
      )}
    </Modal>
  )
}

function RunResult({ run }: { run: RunRecord }) {
  const json = JSON.stringify(run.body, null, 2) ?? 'null'
  // Transport / gateway failures carry the problem+json (or envelope) body; a 200 with
  // `success: false` is a tool-level failure whose message is in `error`.
  const gatewayError = run.status === 'error' && run.httpStatus !== 200 ? run.body : null
  const errMsg =
    run.status === 'error' && !gatewayError && typeof (run.body as ExecuteToolResponse | null)?.error === 'string'
      ? (run.body as ExecuteToolResponse).error
      : null
  return (
    <section className="pg-result" aria-label="Run result">
      <div className="pg-result-head">
        <Badge tone={run.status === 'success' ? 'success' : 'error'}>{run.status}</Badge>
        <span className="cell-sub">{run.httpStatus ? `HTTP ${run.httpStatus}` : 'Network error'}</span>
        <span className="cell-sub" aria-label="Latency">{formatLatency(run.latencyMs)}</span>
        <Link to={`/logs?tool=${encodeURIComponent(run.tool.name)}`} className="link-btn push-right">
          View in Logs <Icon name="external" size={12} />
        </Link>
      </div>
      {gatewayError !== null && <ErrorDetails error={gatewayError} toolkit={run.tool.server_type} />}
      {errMsg && <p className="sf-error" role="alert">{errMsg}</p>}
      <details className="pg-json" open>
        <summary>
          Response
          <CopyButton value={json} label="Copy JSON" />
        </summary>
        <pre><code>{json}</code></pre>
      </details>
    </section>
  )
}

function RunHistory({ runs, onLoad }: { runs: RunRecord[]; onLoad: (r: RunRecord) => void }) {
  if (runs.length === 0) return null
  return (
    <section className="pg-history" aria-label="Run history">
      <h2 className="rail-heading">Recent runs <span className="cell-sub">(this browser session, last {HISTORY_LIMIT})</span></h2>
      <ul>
        {runs.map((r) => (
          <li key={r.id}>
            <button type="button" className="pg-history-row" onClick={() => onLoad(r)} aria-label={`Load run of ${r.tool.name} at ${formatDateTime(r.at)}`}>
              <Badge tone={r.status === 'success' ? 'success' : 'error'}>{r.status}</Badge>
              <code>{r.tool.name}</code>
              <span className="cell-sub push-right">{formatLatency(r.latencyMs)} · {formatDateTime(r.at)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ---------------------------------------------------------------------------

function Playground({ workspaceId }: { workspaceId: string | null }) {
  const formId = useId()
  const queryClient = useQueryClient()
  const { categories: vocab } = useToolCategories()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')
  const debounced = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS)
  // Search results are slim (include_schemas=false); the selected tool's schema is fetched on selection.
  const [schemaLoad, setSchemaLoad] = useState<{ name: string; error?: unknown } | null>(null)
  const selectedName = useRef<string | null>(null)
  const [tool, setTool] = useState<ToolSearchResult | null>(null)
  const [draft, setDraft] = useState<Draft>(undefined)
  const [showErrors, setShowErrors] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [snippetTab, setSnippetTab] = useState<SnippetTab>('TypeScript')
  const [history, setHistory] = useState<RunRecord[]>(() => readHistory(workspaceId))
  const [current, setCurrent] = useState<RunRecord | null>(null)

  const search = useQuery({
    queryKey: qk.toolSearch(workspaceId, debounced, category),
    queryFn: () =>
      apiFetch<ToolSearchResponse>(
        `/tools/search?q=${encodeURIComponent(debounced)}&limit=${SEARCH_LIMIT}&include_schemas=false` +
          (category ? `&category=${encodeURIComponent(category)}` : ''),
      ),
    placeholderData: keepPreviousData,
  })

  const schema = useMemo(() => asSchema(tool?.input_schema), [tool])
  const { args, errors } = useMemo(() => toArguments(schema, draft), [schema, draft])
  const hasErrors = Object.keys(errors).length > 0
  const block = tool ? runBlock(tool) : null
  const risk = tool ? riskOf(tool) : null

  const exec = useMutation({
    mutationFn: async ({ t, a }: { t: ToolSearchResult; a: Record<string, unknown> }): Promise<RunRecord> => {
      const body: ExecuteToolRequest = { arguments: a }
      const started = performance.now()
      const base = { id: newRunId(), tool: t, args: a, at: new Date().toISOString() }
      try {
        const res = await apiFetch<ExecuteToolResponse>(`/tools/${encodeURIComponent(t.name)}/execute`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
        return { ...base, status: statusOf(res), httpStatus: 200, latencyMs: performance.now() - started, body: res }
      } catch (e) {
        // The run history keeps the error, so the mutation itself succeeds: re-read the
        // plan here (a 402 credits_exhausted / 403 plan_restricted means it is stale).
        refreshOnEntitlementDenial(queryClient, e)
        return {
          ...base,
          status: 'error',
          httpStatus: isApiError(e) ? e.status : 0,
          latencyMs: performance.now() - started,
          body: isApiError(e) ? e : { detail: e instanceof Error ? e.message : String(e) },
        }
      }
    },
    onSuccess: (run) => {
      setCurrent(run)
      setHistory((h) => {
        const next = [run, ...h].slice(0, HISTORY_LIMIT)
        writeHistory(workspaceId, next)
        return next
      })
    },
  })

  const select = (t: ToolSearchResult, value?: Record<string, unknown>) => {
    selectedName.current = t.name
    setTool(t)
    setShowErrors(false)
    setConfirming(false)
    setCurrent(null)
    if (t.input_schema) {
      setSchemaLoad(null)
      setDraft(initialDraft(asSchema(t.input_schema), value))
      return
    }
    // Slim result: fetch the full schema now (GET /tools/{tool_name}/schema).
    setSchemaLoad({ name: t.name })
    setDraft(undefined)
    queryClient
      .fetchQuery({ queryKey: qk.toolSchema(workspaceId, t.name), queryFn: () => fetchToolSchema(t.name), staleTime: 5 * 60_000 })
      .then((full) => {
        if (selectedName.current !== t.name) return
        setTool(withSchema(t, full))
        setDraft(initialDraft(asSchema(full.inputSchema), value))
        setSchemaLoad(null)
      })
      .catch((error: unknown) => {
        if (selectedName.current === t.name) setSchemaLoad({ name: t.name, error })
      })
  }

  const schemaPending = !!tool && schemaLoad?.name === tool.name
  const run = () => {
    if (!tool || block || exec.isPending || schemaPending) return
    setShowErrors(true)
    if (hasErrors) return
    if (riskOf(tool) === 'read') exec.mutate({ t: tool, a: args })
    else setConfirming(true)
  }

  if (isApiError(search.error) && search.error.status === 403) {
    return <NotAuthorized what="tools" />
  }

  const snippets = snippetsFor(snippetTab, tool?.name ?? null, args, workspaceId ?? '<WORKSPACE_ID>')

  return (
    <div className="pg">
      {/* Left: code that reproduces the current selection. */}
      <section className="pg-code" aria-label="Code">
        <div className="seg" role="tablist" aria-label="Snippet language">
          {SNIPPET_TABS.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={snippetTab === t} className="seg-btn" onClick={() => setSnippetTab(t)}>
              {t}
            </button>
          ))}
        </div>
        <div role="tabpanel" aria-label={`${snippetTab} snippet`} className="pg-snippets">
          {snippets.map((s) => <CodeBlock key={s.label} code={s.code} label={s.label} />)}
        </div>
        <p className="cell-sub">
          {tool
            ? 'Updates live as you edit arguments. Run it with an API key from this workspace.'
            : 'Pick a tool on the right and this snippet fills in its name and arguments.'}
        </p>
      </section>

      {/* Right: the runner. */}
      <section className="pg-runner" aria-label="Tool runner">
        <div className="pg-search">
          <div className="input-icon">
            <Icon name="search" size={14} />
            <input
              type="search"
              aria-label="Search tools"
              placeholder="Search tools, e.g. “send message”"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {vocab.length > 0 && (
            <label className="chip-select">
              <span className="sr-only">Category</span>
              <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">All categories</option>
                {vocab.map((c) => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
              </select>
            </label>
          )}
        </div>

        <div className="pg-tools-wrap">
          {search.isLoading ? (
            <Loading label="Searching tools" />
          ) : search.error ? (
            <ErrorPanel error={search.error} onRetry={() => search.refetch()} />
          ) : (
            <>
              <ToolList query={debounced} results={search.data?.results ?? []} selected={tool?.name ?? null} onSelect={(t) => select(t)} vocab={vocab} />
              <AvailableHints hints={search.data?.available ?? []} vocab={vocab} />
            </>
          )}
        </div>

        {tool ? (
          <article className="pg-tool-card" aria-label={`Selected tool ${tool.name}`}>
            <header className="pg-tool-card-head">
              <h2><code>{tool.name}</code></h2>
              <div className="badge-row">
                {tool.server_type && <Badge>{tool.server_type}</Badge>}
                {risk && <Badge tone={RISK_BADGE[risk].tone}>{RISK_BADGE[risk].label}</Badge>}
                <Badge tone={connectionBadge(tool.connection_status).tone}>{connectionBadge(tool.connection_status).label}</Badge>
              </div>
              {tool.description && <p className="pg-tool-card-desc">{tool.description}</p>}
            </header>

            {schemaPending && schemaLoad?.error ? (
              <div className="pg-schema-error">
                <h3>Couldn’t load this tool’s schema</h3>
                <ErrorDetails error={schemaLoad.error} toolkit={tool.server_type} />
                <button type="button" className="btn btn--sm" onClick={() => select(tool)}>Retry</button>
              </div>
            ) : schemaPending ? (
              <Loading label="Loading tool schema" />
            ) : block ? (
              <div className="pg-block" role="alert" aria-label={block.title}>
                <Icon name="lock" size={16} />
                <div>
                  <h3>{block.title}</h3>
                  <p>{block.body}</p>
                  {block.action && <Link to={block.action.to} className="btn btn--sm">{block.action.label}</Link>}
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  run()
                }}
                noValidate
              >
                <SchemaForm
                  schema={schema}
                  value={draft}
                  onChange={setDraft}
                  errors={showErrors ? errors : {}}
                  idPrefix={`pg${formId}`}
                />
                {showErrors && hasErrors && (
                  <p className="form-error" role="alert">Fix the highlighted fields before running.</p>
                )}
                <div className="pg-actions">
                  <button type="submit" className="btn btn--primary" disabled={exec.isPending}>
                    {exec.isPending ? <span className="spinner" aria-hidden="true" /> : <Icon name="play" size={14} />}
                    {exec.isPending ? 'Running…' : 'Run tool'}
                  </button>
                  {risk !== 'read' && <span className="cell-sub">You’ll be asked to confirm before it runs.</span>}
                </div>
              </form>
            )}

            {current && current.tool.name === tool.name && <RunResult run={current} />}
          </article>
        ) : (
          (search.data?.results?.length ?? 0) > 0 && (
            <div className="state-panel state-panel--quiet"><p>Select a tool to fill in its arguments and run it.</p></div>
          )
        )}

        <RunHistory runs={history} onLoad={(r) => { select(r.tool, r.args); setCurrent(r) }} />
      </section>

      {confirming && tool && (
        <ConfirmRun
          tool={tool}
          args={args}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false)
            exec.mutate({ t: tool, a: args })
          }}
        />
      )}
    </div>
  )
}

type Mode = 'runner' | 'chat'

export function PlaygroundView() {
  const { workspaceId } = useSession()
  const [mode, setMode] = useState<Mode>('runner')
  return (
    <div className="page page--wide">
      <PageHeader
        title="Playground"
        description="Find a tool, fill in its arguments, run it against your connected accounts, and copy the equivalent SDK or MCP call."
      />
      <div className="tabs" role="tablist" aria-label="Playground mode">
        <button type="button" role="tab" aria-selected={mode === 'runner'} className="tab" onClick={() => setMode('runner')}>
          Tool runner
        </button>
        <button type="button" role="tab" aria-selected={false} aria-disabled="true" disabled className="tab" title="Coming soon">
          Agent chat <Badge tone="info">Soon</Badge>
        </button>
        <span className="tabs-note">Agent chat arrives once the gateway has an agent endpoint.</span>
      </div>
      {/* Keyed so a workspace switch starts clean (selection, history, results). */}
      <Playground key={workspaceId ?? 'none'} workspaceId={workspaceId} />
    </div>
  )
}
